import {
    Autocomplete,
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import useTranslate from 'hooks/useTranslate';
import type { ActivityEventTypeOption } from 'types';
import { toDate, toDateString } from 'utils/dateUtils';

export interface EventFiltersProps {
    search: string;
    onSearchChange: (value: string) => void;
    selectedTypes: ActivityEventTypeOption[];
    typeOptions: ActivityEventTypeOption[];
    onTypesChange: (value: ActivityEventTypeOption[]) => void;
    startDate: string;
    onStartDateChange: (value: string) => void;
    endDate: string;
    onEndDateChange: (value: string) => void;
    sortOrder: 'asc' | 'desc';
    onSortOrderChange: (value: 'asc' | 'desc') => void;
}

const fieldSx = { minWidth: 0 };

const datePickerSlotProps = {
    field: { clearable: true },
    textField: {
        size: 'small' as const,
        fullWidth: true as const,
    },
};

export const EventFilters = ({
    search,
    onSearchChange,
    selectedTypes,
    typeOptions,
    onTypesChange,
    startDate,
    onStartDateChange,
    endDate,
    onEndDateChange,
    sortOrder,
    onSortOrderChange,
}: EventFiltersProps) => {
    const { translate } = useTranslate();

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'minmax(0, 1.4fr) minmax(0, 1.6fr) repeat(3, minmax(0, 1fr))',
                },
                gap: 1.5,
                width: '100%',
                minWidth: 0,
            }}
        >
            <TextField
                size="small"
                fullWidth
                label={translate('nameOrEmail')}
                placeholder={translate('nameOrEmail')}
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                sx={fieldSx}
            />
            <Autocomplete
                multiple
                size="small"
                options={typeOptions}
                value={selectedTypes}
                onChange={(_, value) => onTypesChange(value)}
                groupBy={(option) =>
                    option.category
                        ? option.category.charAt(0).toUpperCase() + option.category.slice(1)
                        : option.category
                }
                getOptionLabel={(option) => option.label || option.value}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                limitTags={1}
                fullWidth
                renderInput={(params) => <TextField {...params} label={translate('eventType')} />}
                sx={fieldSx}
            />
            <DatePicker
                label={translate('startDate')}
                format="dd/MM/yyyy"
                value={toDate(startDate)}
                maxDate={toDate(endDate) ?? undefined}
                onChange={(value: Date | null) => onStartDateChange(toDateString(value))}
                slotProps={datePickerSlotProps}
                sx={fieldSx}
            />
            <DatePicker
                label={translate('endDate')}
                format="dd/MM/yyyy"
                value={toDate(endDate)}
                minDate={toDate(startDate) ?? undefined}
                onChange={(value: Date | null) => onEndDateChange(toDateString(value))}
                slotProps={datePickerSlotProps}
                sx={fieldSx}
            />
            <FormControl size="small" fullWidth sx={fieldSx}>
                <InputLabel id="event-tracker-sort-label">{translate('sortBy')}</InputLabel>
                <Select
                    labelId="event-tracker-sort-label"
                    label={translate('sortBy')}
                    value={sortOrder}
                    onChange={(event) =>
                        onSortOrderChange(event.target.value === 'asc' ? 'asc' : 'desc')
                    }
                >
                    <MenuItem value="desc">{translate('sortNewest')}</MenuItem>
                    <MenuItem value="asc">{translate('sortOldest')}</MenuItem>
                </Select>
            </FormControl>
        </Box>
    );
};
